import React, { useRef, useState, useEffect } from 'react';
import { Printer, Edit2, Save, X } from 'lucide-react';
import apiService from '../../../config';

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Returns today as "DD/MM/YYYY" in English digits
const getTodayEnglish = () => {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = String(now.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
};

// "DD/MM/YYYY" → "YYYY-MM-DD" for <input type="date"> and API payload
const toInputDate = (dateStr) => {
  const todayISO = () => {
    const n = new Date();
    const dd = String(n.getDate()).padStart(2, '0');
    const mm = String(n.getMonth() + 1).padStart(2, '0');
    return `${n.getFullYear()}-${mm}-${dd}`;
  };
  if (!dateStr) return todayISO();
  // Already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  // DD/MM/YYYY format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    const [dd, mm, yyyy] = dateStr.split('/');
    return `${yyyy}-${mm}-${dd}`;
  }
  // Fallback → today
  return todayISO();
};

// "YYYY-MM-DD" → "DD/MM/YYYY" in English digits
const fromInputDate = (isoDate) => {
  if (!isoDate) return getTodayEnglish();
  const [yyyy, mm, dd] = isoDate.split('-');
  return `${dd}/${mm}/${yyyy}`;
};

// ─── Default Values ───────────────────────────────────────────────────────────
const DEFAULT_DATA = {
  address: `उपकार्यकारी अभियंता, (बांधकाम विभाग), दक्षिण मध्य रेल्वे, अकोला`,
  date: getTodayEnglish(),
  subject: `खाजगी जमिनी संपादन करण्याबाबतीत प्रस्ताव.`,
  contents: `अकोट-आमलाखुर्द गेज परिवर्तन प्रस्तावित ब्रॉडगेजसाठी भुसंपादन प्रस्तावातील बुलढाणा जिल्हातील तेरा गावामधील जमिन संपादन करणे आवश्यक आहे. सदर भुसंपादन करण्याबाबत आवश्यक तो ठराव /मंजूरी घेण्यात आलेली आहे. नमूना 'क' | 'ख' । 'ग' मधील सर्व माहितीचा तपाशील दिला आहे. तसेच त्याबाबतीत आवश्यक असलेल्या कागदपत्राच्या व नकाशाच्या प्रतिही सोबत जोडल्या आहेत, ह्या भुसंपादनबाबत रेल्वे खात्याकडून कोणतीही माहिती व सहकार्य आवश्यक वाटल्यास त्याकरिता या खात्यातील श्री. पुरुषोत्तम, मो.नं. ०९७०१३७२१९३, उपकार्यकारी अभियंता (बांधकाम विभाग), अकोला ह्यांची 'संपर्क अधिकारी' पदी नेमणूक करण्यात येत आहे. यांचा कार्यालयीन पत्ताः उपकार्यकारी अभियंता (बांधकाम विभाग), दक्षिण मध्य रेल्वे, अकोला असा आहे.\n\nतरी आपणांस विनंती करण्यात येते की, सदर प्रस्तावानुसार जमिनीचे संपादन करण्याबाबत आवश्यक ती कार्यवाही करावी.`,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getProjectInfo = () => {
  try {
    const currentProjectData = localStorage.getItem('currentProjectData');
    if (currentProjectData) {
      const p = JSON.parse(currentProjectData);
      return { id: p.project_id || p.id, name: p.project_name || p.name };
    }
    const selectedProject = localStorage.getItem('selectedProject');
    if (selectedProject) {
      const p = JSON.parse(selectedProject);
      return { id: p.project_id || p.id, name: p.project_name || p.name };
    }
    const userData = localStorage.getItem('userData');
    if (userData) {
      const u = JSON.parse(userData);
      return {
        id: u.data?.project_id || u.project_id,
        name: u.data?.project_name || u.project_name || 'Unknown Project',
      };
    }
  } catch (e) {
    console.error('getProjectInfo error:', e);
  }
  return { id: null, name: null };
};

const getProposalInfo = () => {
  try {
    const u = JSON.parse(localStorage.getItem('userData') || '{}');
    return {
      proposalId:
        u?.data?.default_proposal ||
        u?.data?.proposal_id ||
        u?.default_proposal ||
        u?.proposal_id ||
        null,
    };
  } catch (e) {
    console.error('getProposalInfo error:', e);
    return { proposalId: null };
  }
};

// ─── Component ────────────────────────────────────────────────────────────────
const Parishtisht_A = () => {
  const documentRef = useRef(null);

  // IDs
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentProposalId, setCurrentProposalId] = useState(null);

  // Always compute today fresh at render time
  const todayEnglish = getTodayEnglish();

  // Data & UI state — date always defaults to today
  const [apiData, setApiData] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_DATA, date: todayEnglish });
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ ...DEFAULT_DATA, date: todayEnglish });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ── Load IDs once ────────────────────────────────────────────────────────
  useEffect(() => {
    const proj = getProjectInfo();
    const prop = getProposalInfo();
    setCurrentProjectId(proj.id);
    setCurrentProposalId(prop.proposalId);
  }, []);

  // ── Fetch API data when IDs are ready ────────────────────────────────────
  useEffect(() => {
    if (!currentProjectId || !currentProposalId) return;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const record = await apiService.getRailwayParishistANotice(currentProjectId, currentProposalId);
        if (record && record.notice_id) {
          setApiData(record);
          const mapped = {
            address: record.address || DEFAULT_DATA.address,
            date: record.date ? fromInputDate(record.date) : getTodayEnglish(),
            subject: record.subject || DEFAULT_DATA.subject,
            contents: record.contents || DEFAULT_DATA.contents,
          };
          setFormData(mapped);
          setEditForm(mapped);
        }
        // else: no record → keep defaults
      } catch (e) {
        console.error('Fetch Parishist A error:', e);
        setError('डेटा लोड करताना त्रुटी आली. डीफॉल्ट माहिती दाखवत आहे.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentProjectId, currentProposalId]);

  // ── Save (POST) ──────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        project_id: currentProjectId,
        proposal_id: currentProposalId,
        address: editForm.address,
        date: toInputDate(editForm.date || todayEnglish),   // send as YYYY-MM-DD to API
        subject: editForm.subject,
        contents: editForm.contents,
        ...(apiData?.notice_id ? { notice_id: apiData.notice_id } : {}),
      };

      const updateData = await apiService.postAddUpdateParishistANotice(payload);

      if (updateData) {
        setApiData(updateData);
        const mapped = {
          address: updateData.address || editForm.address,
          date: updateData.date ? fromInputDate(updateData.date) : editForm.date || getTodayEnglish(),
          subject: updateData.subject || editForm.subject,
          contents: updateData.contents || editForm.contents,
        };
        setFormData(mapped);
        setEditForm(mapped);
      } else {
        setFormData({ ...editForm });
      }
      setIsEditing(false);
    } catch (e) {
      console.error('Save Parishist A error:', e);
      setError('डेटा सेव्ह करताना त्रुटी आली.');
    } finally {
      setSaving(false);
    }
  };

  // ── Print ────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    const printContent = documentRef.current?.innerHTML;
    if (!printContent) { alert('No content available to print.'); return; }
    const win = window.open('', '', 'height=900,width=1200');
    win.document.write(`
      <html>
        <head>
          <title>परिशिष्ट "अ"</title>
          <style>
            @page { size: A4 portrait; margin-top:.3in; margin-bottom:.3in; margin-left:.75in; margin-right:.6in; }
            body { font-family:'Noto Sans Devanagari','Mangal',Arial,sans-serif; font-size:13px; line-height:1.6; color:#000; margin:0; padding:0; }
            .document-container { max-width:100%; margin:0; padding:0; }
            .text-center { text-align:center; }
            .text-right { text-align:right; }
            .font-bold { font-weight:bold; }
            .underline { text-decoration:underline; }
            .mb-1{margin-bottom:4px;}.mb-2{margin-bottom:8px;}.mb-3{margin-bottom:12px;}.mb-4{margin-bottom:16px;}.mb-6{margin-bottom:24px;}.mb-8{margin-bottom:32px;}.mb-12{margin-bottom:48px;}
            .mt-2{margin-top:8px;}.mt-4{margin-top:16px;}.mt-8{margin-top:32px;}.mt-12{margin-top:48px;}
            .ml-8{margin-left:32px;}.ml-16{margin-left:64px;}
            .text-justify{text-align:justify;text-justify:inter-word;}
            .signature-space{height:60px;}
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    win.document.close();
    win.print();
  };

  // ── Edit Modal — same structure as PrathmikAdhisuchna's renderEditModal ──
  const renderEditModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">

          {/* Modal Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">परिशिष्ट "अ" दुरस्ती</h2>
            <button
              onClick={() => { setEditForm(formData); setIsEditing(false); }}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>

            {/* ── Section: कार्यालयीन तपशील ── */}
            <div className="mb-6">
              <div className="flex items-center justify-center mb-6">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-full">
                  <h2 className="font-bold text-lg">कार्यालयीन तपशील</h2>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="group">
                  <label className="block font-semibold text-gray-700 mb-2 text-base">
                    पत्ता (Address)
                  </label>
                  <textarea
                    rows={3}
                    value={editForm.address}
                    onChange={e => setEditForm({ ...editForm, address: e.target.value })}
                    placeholder="कार्यालयाचा पत्ता..."
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none group-hover:border-gray-300"
                    style={{ fontFamily: "'Noto Sans Devanagari','Mangal',Arial,sans-serif", fontSize: '14px' }}
                  />
                </div>

                <div className="group">
                  <label className="block font-semibold text-gray-700 mb-2 text-base">
                    दिनांक (Date)
                  </label>
                  <input
                    type="date"
                    value={toInputDate(editForm.date)}
                    onChange={e => setEditForm({ ...editForm, date: fromInputDate(e.target.value) })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 group-hover:border-gray-300"
                    style={{ fontFamily: "'Noto Sans Devanagari','Mangal',Arial,sans-serif", fontSize: '14px' }}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    निवडलेली तारीख: {editForm.date}
                  </p>
                </div>
              </div>
            </div>

            {/* ── Section: विषय ── */}
            <div className="mb-6">
              <div className="flex items-center justify-center mb-6">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-full">
                  <h2 className="font-bold text-lg">विषय</h2>
                </div>
              </div>

              <div className="group">
                <label className="block font-semibold text-gray-700 mb-2 text-base">
                  विषय (Subject)
                </label>
                <input
                  type="text"
                  value={editForm.subject}
                  onChange={e => setEditForm({ ...editForm, subject: e.target.value })}
                  placeholder="विषय टाका..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 group-hover:border-gray-300"
                  style={{ fontFamily: "'Noto Sans Devanagari','Mangal',Arial,sans-serif", fontSize: '14px' }}
                />
              </div>
            </div>

            {/* ── Section: मजकूर ── */}
            <div className="mb-6">
              <div className="flex items-center justify-center mb-6">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-full">
                  <h2 className="font-bold text-lg">मजकूर</h2>
                </div>
              </div>

              <div className="group">
                <label className="block font-semibold text-gray-700 mb-2 text-base">
                  मजकूर (Contents)
                  <span className="text-gray-400 text-xs font-normal ml-2">
                    — दोन परिच्छेद वेगळे करण्यासाठी दोनदा Enter दाबा
                  </span>
                </label>
                <textarea
                  rows={8}
                  value={editForm.contents}
                  onChange={e => setEditForm({ ...editForm, contents: e.target.value })}
                  placeholder="पत्राचा मजकूर टाका..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none group-hover:border-gray-300"
                  style={{ fontFamily: "'Noto Sans Devanagari','Mangal',Arial,sans-serif", fontSize: '14px' }}
                />
              </div>
            </div>

            {/* ── Action Buttons — exact same style as PrathmikAdhisuchna ── */}
            <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => { setEditForm(formData); setIsEditing(false); }}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 transform hover:scale-105"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 flex items-center justify-center space-x-3 shadow-lg"
              >
                {saving && (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                )}
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );

  const contentParagraphs = formData.contents.split('\n\n').filter(Boolean);

  // ── Main Render ──────────────────────────────────────────────────────────
  return (
    <>
      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-4xl mx-auto p-4">

          {/* Top action bar — same style as PrathmikAdhisuchna */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex justify-between items-center">
              <div />
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handlePrint}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                >
                  <span>Print</span>
                </button>

                <button
                  type="button"
                  onClick={() => { setEditForm({ ...formData, date: formData.date || todayEnglish }); setIsEditing(true); }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                >
                  <span>Edit</span>
                </button>
              </div>
            </div>
          </div>

          {/* Loading / error (shown only when NOT in edit modal) */}
          {loading && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
              डेटा लोड होत आहे...
            </div>
          )}
          {error && !isEditing && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Document Preview */}
          <div className="bg-white shadow-2xl rounded-lg overflow-hidden">
            <div
              ref={documentRef}
              className="document-container"
              style={{
                fontFamily: "'Noto Sans Devanagari','Mangal',Arial,sans-serif",
                maxWidth: '800px',
                margin: '0 auto',
                padding: '40px',
                fontSize: '14px',
                lineHeight: '1.6',
              }}
            >
              {/* Title */}
              <div className="text-center font-bold mb-8 underline">परिशिष्ट "अ"</div>

              {/* Address */}
              <div className="text-right mb-8">
                {formData.address.split('\n').filter(Boolean).map((line, i) => (
                  <div key={i} className="mb-1">{line}</div>
                ))}
                <div className="mb-3">दिनांक : {formData.date}</div>
              </div>

              {/* To */}
              <div className="mb-4"><span className="font-bold">प्रति,</span></div>
              <div className="mb-6"><span className="font-bold">मा.जिल्हाधिकारी, बुलढाणा.</span></div>

              {/* Subject */}
              <div className="mb-4 ml-8">
                <span className="font-bold underline">विषय : </span>
                <span className="font-bold underline">{formData.subject}</span>
              </div>

              {/* Salutation */}
              <div className="mb-4"><span className="font-bold">महोदय,</span></div>

              {/* Contents */}
              {contentParagraphs.map((para, i) => (
                <div key={i} className="mb-4 ml-16" style={{ textAlign: 'justify' }}>
                  {para.trim()}
                </div>
              ))}

              {/* Closing */}
              <div className="text-right mt-8 mb-12">
                <div className="mb-3">आपला नम्र,</div>
                <div style={{ height: '60px' }} />
                <div className="mb-1 font-bold">उपकार्यकारी अभियंता,</div>
                <div className="mb-1 font-bold">(बांधकाम विभाग),</div>
                <div className="font-bold">दक्षिण मध्य रेल्वे, अकोला</div>
              </div>

              {/* Attachments */}
              <div className="mt-8">
                <div className="mb-2 font-bold underline">सोबत :-</div>
                <div className="ml-8 mb-1">१. नमूना क, ख, ग</div>
                <div className="ml-8">२. कागदपत्रे व नकाशा</div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Edit Modal — rendered outside main flow, same as PrathmikAdhisuchna */}
      {isEditing && renderEditModal()}
    </>
  );
};

export default Parishtisht_A;